import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { confirmAsync, pushToast } from "@/components/Toaster";
import {
  type ThrottleRow,
  useResetThrottle,
  useThrottle,
  useUpsertThrottle,
} from "@/lib/throttle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { StatusBadge } from "@/components/ui/badge";
import { HelpSection } from "@/components/HelpSection";

export function ThrottleSection() {
  const { t } = useTranslation();
  const rows = useThrottle();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-fg-muted">{t("throttle.subtitle")}</p>
      </div>

      <Alert tone="warning" title={t("throttle.warning_title")}>
        {t("throttle.warning_body")}
      </Alert>

      {rows.isLoading && (
        <p className="text-fg-muted text-sm">{t("common.loading")}</p>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        {(rows.data ?? []).map((r, i) => (
          <ProfileRow key={r.profile} row={r} isFirst={i === 0} />
        ))}
      </div>

      <p className="text-xs text-fg-muted">{t("throttle.infinity_hint")}</p>

      <HelpSection
        i18nKey="throttle.help"
        paragraphs={3}
        list={{
          titleKey: "throttle.help.list_title",
          itemsKey: "throttle.help.list_items",
          itemCount: 3,
        }}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- ProfileRow */

function ProfileRow({ row, isFirst }: { row: ThrottleRow; isFirst: boolean }) {
  const { t } = useTranslation();
  const upsert = useUpsertThrottle();
  const reset = useResetThrottle();
  const [minD, setMinD] = useState(String(row.min_delay_seconds));
  const [maxD, setMaxD] = useState(String(row.max_delay_seconds));
  const [perDay, setPerDay] = useState(
    row.max_per_account_per_day != null ? String(row.max_per_account_per_day) : "",
  );
  const [error, setError] = useState<string | null>(null);

  /* FIX: sync local state when row data changes (after reset — was stale until F5) */
  useEffect(() => {
    setMinD(String(row.min_delay_seconds));
    setMaxD(String(row.max_delay_seconds));
    setPerDay(
      row.max_per_account_per_day != null ? String(row.max_per_account_per_day) : "",
    );
  }, [row.min_delay_seconds, row.max_delay_seconds, row.max_per_account_per_day]);

  /* inline min ≤ max validation */
  const minNum = Number(minD);
  const maxNum = Number(maxD);
  const minMaxError =
    minD && maxD && !isNaN(minNum) && !isNaN(maxNum) && minNum > maxNum
      ? t("throttle.validation.min_max")
      : null;

  const isInviting = row.profile === "inviting";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (minMaxError) return;
    setError(null);
    try {
      await upsert.mutateAsync({
        profile: row.profile,
        min_delay_seconds: Number(minD),
        max_delay_seconds: Number(maxD),
        max_per_account_per_day: isInviting && perDay ? Number(perDay) : null,
      });
      pushToast({ kind: "success", text: t("throttle.save_success") });
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("throttle.save_error"));
    }
  };

  const onReset = async () => {
    const yes = await confirmAsync({
      title: t("throttle.reset_confirm", {
        profile: t(`throttle.profile.${row.profile}`),
      }),
      confirmKind: "danger",
      confirmLabel: t("throttle.reset"),
    });
    if (!yes) return;
    setError(null);
    try {
      await reset.mutateAsync(row.profile);
      pushToast({ kind: "success", text: t("throttle.reset_success") });
      /* local state will sync via the useEffect above once the query re-fetches */
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("throttle.save_error"));
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className={`p-5 space-y-4 bg-bg-secondary ${!isFirst ? "border-t border-border" : ""}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-[12rem] flex-1">
          <p className="font-semibold text-sm">{t(`throttle.profile.${row.profile}`)}</p>
          <p className="text-xs text-fg-muted mt-0.5">
            {t(`throttle.profile_hint.${row.profile}`)}
          </p>
        </div>
        <StatusBadge
          domain="throttle"
          value={row.is_default ? "default" : "override"}
          label={
            row.is_default ? t("throttle.default_label") : t("throttle.override_label")
          }
        />
      </div>

      <div className={`grid items-end gap-3 ${isInviting ? "grid-cols-3" : "grid-cols-2"}`}>
        <div className="space-y-1.5">
          <Label htmlFor={`min-${row.profile}`} className="text-xs">
            {t("throttle.field.min_delay")}
          </Label>
          <Input
            id={`min-${row.profile}`}
            inputMode="decimal"
            value={minD}
            onChange={(e) => setMinD(e.target.value)}
            className={minMaxError ? "border-danger" : ""}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`max-${row.profile}`} className="text-xs">
            {t("throttle.field.max_delay")}
          </Label>
          <Input
            id={`max-${row.profile}`}
            inputMode="decimal"
            value={maxD}
            onChange={(e) => setMaxD(e.target.value)}
            className={minMaxError ? "border-danger" : ""}
          />
        </div>
        {/* A daily limit exists only for sending: scraping and warmup have
            their own built-in budgets and never read this field. */}
        {isInviting && (
          <div className="space-y-1.5">
            <Label htmlFor={`day-${row.profile}`} className="text-xs">
              {t("throttle.field.per_day")}
            </Label>
            <Input
              id={`day-${row.profile}`}
              inputMode="numeric"
              placeholder={t("throttle.field.per_day_placeholder") ?? "8"}
              value={perDay}
              onChange={(e) => {
                // The server accepts 1–200; keep the field inside that range.
                const digits = e.target.value.replace(/\D/g, "");
                setPerDay(digits === "" ? "" : String(Math.min(200, Number(digits))));
              }}
            />
          </div>
        )}
      </div>
      {isInviting && (
        <p className="text-xs text-fg-muted">{t("throttle.field.per_day_hint")}</p>
      )}

      {minMaxError && (
        <p className="text-xs text-danger">{minMaxError}</p>
      )}
      {error && <p className="text-xs text-danger break-all">{error}</p>}

      <div className="flex justify-end gap-2">
        {!row.is_default && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onReset}
            loading={reset.isPending}
          >
            {t("throttle.reset")}
          </Button>
        )}
        <Button
          type="submit"
          size="sm"
          loading={upsert.isPending}
          disabled={!!minMaxError}
        >
          {t("throttle.save")}
        </Button>
      </div>
    </form>
  );
}
